import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Mail,
  MessageSquare,
  HelpCircle,
  Settings,
  CreditCard,
  Zap,
  CheckCircle,
  Send,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useTheme } from '../contexts/ThemeContext';
import { axFetch } from '../lib/utils';
import { Banner, Navbar, Footer } from '../components/landing/Shared';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/ui/authModal';
import { useNavigate } from 'react-router-dom';

const Support: React.FC = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const { isAuthenticated, logout } = useAuth();
  
  // Navbar state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [modalMode, setModalMode] = useState<'signin' | 'signup'>('signin');


  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    category: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Navbar handlers
  const handleSignIn = () => {
    setModalMode('signin');
    setShowSignInModal(true);
  };

  const handleSignUp = () => {
    setModalMode('signup');
    setShowSignInModal(true);
  };

  const handleSignOut = () => {
    logout();
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const handleRunClick = () => {
    navigate('/#download');
  };

  const supportCategories = [
    {
      id: 'general',
      label: 'General Inquiry',
      icon: <HelpCircle className="w-6 h-6" />,
      description: 'Questions about our platform and services',
      color: 'blue',
      // Using static colors matching the new theme style
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      hoverBg: 'hover:bg-blue-100',
      selectedBg: 'bg-zinc-900', // Black for selected like new buttons
      selectedBorder: 'border-zinc-900'
    },
    {
      id: 'technical',
      label: 'Technical Support',
      icon: <Settings className="w-6 h-6" />,
      description: 'Issues with features, bugs, or technical problems',
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      hoverBg: 'hover:bg-green-100',
      selectedBg: 'bg-zinc-900',
      selectedBorder: 'border-zinc-900'
    },
    {
      id: 'billing',
      label: 'Billing & Account',
      icon: <CreditCard className="w-6 h-6" />,
      description: 'Questions about pricing, invoices, or account management',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      hoverBg: 'hover:bg-purple-100',
      selectedBg: 'bg-zinc-900',
      selectedBorder: 'border-zinc-900'
    },
    {
      id: 'feature',
      label: 'Feature Request',
      icon: <Zap className="w-6 h-6" />,
      description: 'Suggestions for new features or improvements',
      color: 'orange',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      hoverBg: 'hover:bg-orange-100',
      selectedBg: 'bg-zinc-900',
      selectedBorder: 'border-zinc-900'
    }
  ];


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactForm.email.trim() || !contactForm.message.trim() || !contactForm.subject.trim()) {
      setSubmitError('Please fill in all required fields (email, subject, and message)');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      setSubmitError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axFetch('POST', 'contact-us', {
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        subject: contactForm.subject,
        category: contactForm.category,
        message: contactForm.message
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        responseData = {};
      }

      if (!response.ok) {
        throw new Error(responseData.message || responseData.error || 'Failed to send message');
      }

      setContactForm({
        name: '',
        email: '',
        company: '',
        subject: '',
        category: 'general',
        message: ''
      });

      setSubmitSuccess(true);

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 8000);

    } catch (error) {
      console.error('Error sending support message:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to send message. Please try again.');
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#111] antialiased font-sans">
      <Banner />
      <Navbar 
        onSignIn={handleSignIn} 
        onSignUp={handleSignUp} 
        onRunClick={handleRunClick}
        isAuthenticated={isAuthenticated}
        onSignOut={handleSignOut}
        onDashboard={handleDashboard}
      />
      
      <AuthModal
        showModal={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        initialMode={modalMode}
      />

      <div className="pt-32 pb-20 max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-2xl shadow-sm">
              <HelpCircle className="w-10 h-10 text-zinc-900" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-[#111] mb-4 tracking-tight">
            How can we help you?
          </h1>
          <p className="text-lg text-zinc-500 max-w-2xl mx-auto">
            Get support, find answers, or reach out to our team.
          </p>
        </div>

        {/* Contact Form Section */}
        <div>
          <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 md:p-12">
            <h2 className="text-2xl font-bold text-zinc-900 mb-6">
              Send us a message
            </h2>

            {submitSuccess && (
              <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Message sent successfully! We'll get back to you soon.</span>
                </div>
              </div>
            )}

            {submitError && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span>{submitError}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-zinc-900 mb-3">
                  What can we help you with?
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {supportCategories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => {
                        setContactForm(prev => ({ ...prev, category: category.id }));
                      }}
                      className={`group relative p-4 rounded-xl text-left transition-all duration-200 border ${contactForm.category === category.id
                        ? `${category.selectedBg} ${category.selectedBorder} text-white shadow-md scale-[1.02]`
                        : `bg-white border-zinc-200 text-zinc-900 hover:border-zinc-300 hover:shadow-sm active:scale-[0.98]`
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 transition-colors ${contactForm.category === category.id ? 'text-white' : 'text-zinc-500'}`}>
                          {category.icon}
                        </div>
                        <div className="flex-1">
                          <div className={`font-semibold text-sm mb-0.5 ${contactForm.category === category.id ? 'text-white' : 'text-zinc-900'}`}>
                            {category.label}
                          </div>
                          <div className={`text-xs ${contactForm.category === category.id ? 'text-zinc-300' : 'text-zinc-500'}`}>
                            {category.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Name and Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-zinc-900 mb-1.5">
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={contactForm.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="w-full bg-white border-zinc-300 focus:ring-zinc-900"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-zinc-900 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={contactForm.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                    className="w-full bg-white border-zinc-300 focus:ring-zinc-900"
                  />
                </div>
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-zinc-900 mb-1.5">
                  Company (Optional)
                </label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={contactForm.company}
                  onChange={handleInputChange}
                  placeholder="Your company name"
                  className="w-full bg-white border-zinc-300 focus:ring-zinc-900"
                />
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-zinc-900 mb-1.5">
                  Subject <span className="text-red-500">*</span>
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={contactForm.subject}
                  onChange={handleInputChange}
                  placeholder="Brief description of your issue"
                  required
                  className="w-full bg-white border-zinc-300 focus:ring-zinc-900"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-zinc-900 mb-1.5">
                  Message <span className="text-red-500">*</span>
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleInputChange}
                  placeholder="Please provide as much detail as possible..."
                  required
                  rows={6}
                  className="w-full bg-white border-zinc-300 focus:ring-zinc-900"
                />
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-zinc-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>We typically respond within 24 hours</span>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="min-w-[140px] bg-zinc-900 hover:bg-zinc-800 text-white shadow-lg transition-all active:scale-95"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      <span>Send Message</span>
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Alternative Contact */}
            <div className="mt-8 pt-8 border-t border-zinc-200 text-center">
              <p className="text-sm text-zinc-500 mb-6">
                Prefer to email us directly?
              </p>
              <a
                href="mailto:team@alt-x.co"
                className="inline-flex items-center gap-2 text-brand-blue hover:text-blue-700 hover:underline px-4 py-2 rounded-lg bg-blue-50 transition-all duration-200 hover:bg-blue-100"
              >
                <Mail className="w-4 h-4" />
                team@alt-x.co
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Support;
